import React from "react";
import { classNames } from "./Utils";
import * as MD from "reports/measures/MeasureDef";
import * as MP from "reports/measures/MeasureProperties";
import * as SR from "reports/measures/SeverityResolver";

const cleanLabel = (label) => {
    let limit = 20;
    if (label.length <= limit) {
        return <div className="text-gray-700 text-sm font-medium leading-5">{label}</div>;
    }
    return (
        <div className="text-gray-700 text-sm font-medium relative has-tooltip leading-5 flex">
            <span className="tooltip rounded shadow-lg px-1 py-0.5 ml-10 mt-4 w-max">{label}</span>
            <div className="hs-tooltip">{label.substring(0, limit) + "..."}</div>
        </div>
    );
};

export const MotionRomTypeMap = Object.freeze({
    "LeftShoulderAbduction": MD.MeasureTypesMap.LeftAbduction,
    "LeftShoulderFlexion": MD.MeasureTypesMap.LeftFlexion,
    "LeftShoulderScaption": MD.MeasureTypesMap.LeftScaption,
    "RightShoulderAbduction": MD.MeasureTypesMap.RightAbduction,
    "RightShoulderFlexion": MD.MeasureTypesMap.RightFlexion,
    "RightShoulderScaption": MD.MeasureTypesMap.RightScaption,
    "BackFlexion": MD.MeasureTypesMap.LowerFlexion,
    "NeckExtension": MD.MeasureTypesMap.NeckExtension,
    "NeckFlexion": MD.MeasureTypesMap.NeckFlexion,
    "NeckLeftRotation": MD.MeasureTypesMap.NeckLeftRotation,
    "NeckRightRotation": MD.MeasureTypesMap.NeckRightRotation
});

export const isUpperExtremityRom = (romType) => MP.MeasurePropertiesMap[romType].categories.includes(MD.MeasureCategory.UPPER_ROM);
export const isBackRom = (romType) => MP.MeasurePropertiesMap[romType].categories.includes(MD.MeasureCategory.LOWER_ROM);
export const isNeckRom = (romType) => MP.MeasurePropertiesMap[romType].categories.includes(MD.MeasureCategory.NECK_ROM);
export const romMaxValue = (romType) => MP.MeasurePropertiesMap[romType].maxScore;
export const romNormalizedValue = (romMeasureType, romValue) => Math.round((Math.min(Math.round(romValue), MP.MeasurePropertiesMap[romMeasureType].maxScore) / MP.MeasurePropertiesMap[romMeasureType].maxScore) * 100);

const getSeverityDetails = (romMeasureType, romValue) =>
    MP.MeasurePropertiesMap[romMeasureType].severityResolver(romValue);

export const romBarColor = (romMeasureType, romValue) => {
    const details = getSeverityDetails(romMeasureType, romValue);
    const colorCat = SR.getSeverityColor(romMeasureType, details.severityLevel);

    if (colorCat === 'severe') return "bg-error-300";
    if (colorCat === 'warn') return "bg-warning-300";
    return "bg-success-300";
};

export const romBadgeTextColor = (romMeasureType, romValue) => {
    const details = getSeverityDetails(romMeasureType, romValue);
    const colorCat = SR.getSeverityColor(romMeasureType, details.severityLevel);

    if (colorCat === 'severe') return "text-error-700";
    if (colorCat === 'warn') return "text-warning-700";
    return "text-success-700";
};

export const romBadgeColor = (romMeasureType, romValue) => {
    const details = getSeverityDetails(romMeasureType, romValue);
    const colorCat = SR.getSeverityColor(romMeasureType, details.severityLevel);

    if (colorCat === 'severe') return "bg-error-50";
    if (colorCat === 'warn') return "bg-warning-50";
    return "bg-success-50";
};

export const romLevel = (romMeasureType, romValue) => {
    const details = getSeverityDetails(romMeasureType, romValue);
    return SR.getSeverityLabel(romMeasureType, details.severityLevel);
};

export const romTooltip = (romMeasureType, romValue) => {
    const details = getSeverityDetails(romMeasureType, romValue);
    // Return the label from the resolver - labels from Confluence now include the complete text
    return details.label || "";
};

export const renderRomRow = (key, romLabel, romMeasureType, romValue) => {
    return (
        <React.Fragment key={key}>
            <div className="flex items-center gap-2 border-red-600 border-2">
                <img className="w-5 h-5" src="/icons/compass.svg" alt="icon" />
                {cleanLabel(romLabel)}
            </div>
            <div className="flex items-center gap-4 w-full">
                <div className="grow shrink relative h-5 flex items-center">
                    <div className="w-full absolute h-2 bg-gray-200 rounded-full" />
                    <div className={classNames("h-2 absolute rounded-full", romBarColor(romMeasureType, romValue))} style={{ width: `${romNormalizedValue(romMeasureType, romValue)}%` }} />
                </div>
                <span className="inline-flex gap-2 text-sm items-center">
                    {`${Math.round(romValue)}°`}
                    <div className={`px-2 py-0.5 has-tooltip relative rounded-2xl justify-center items-center flex ${romBadgeColor(romMeasureType, romValue)}`}>
                        <span className="tooltip rounded shadow-lg px-1 py-0.5 -mt-12 w-max">{romTooltip(romMeasureType, romValue)}</span>
                        <div className={`text-center hs-tooltip text-xs font-medium leading-[18px] text-nowrap ${romBadgeTextColor(romMeasureType, romValue)}`}>{romLevel(romMeasureType, romValue)}</div>
                    </div>
                </span>
            </div>
        </React.Fragment>
    );
};